import type { QuestionnaireTurnCommand } from "@digital-twins/shared-types";
import type { StudyObjectiveChange } from "@digital-twins/shared-types/questionnaire/revision.generated";

import i18n from "@/i18n";

/** A language code's name in the chat's language (the interface's with none). */
export function languageName(code: string, lng?: string): string {
  try {
    return (
      new Intl.DisplayNames([lng || i18n.language || "en"], {
        type: "language",
      }).of(code) ?? code
    );
  } catch {
    return code;
  }
}

/**
 * The builder's words for what an objective change changed: the decision's
 * new words, then each learning objective added and removed: the objective
 * panel's command, in the user's own bubble. A chat reply that changed the
 * objective states it as a receipt (Nest, `chg_objective_decision`).
 */
export function objectiveChangeLines(
  change: StudyObjectiveChange,
  lng?: string,
): Array<string> {
  return [
    ...(change.decision
      ? [
          i18n.t("questionnaire.command.setObjective", {
            ns: "app",
            lng,
            decision: change.decision.current,
          }),
        ]
      : []),
    ...change.addedLearn.map((text) =>
      i18n.t("questionnaire.command.objectiveAdded", { ns: "app", lng, text }),
    ),
    ...change.removedLearn.map((text) =>
      i18n.t("questionnaire.command.objectiveRemoved", { ns: "app", lng, text }),
    ),
  ];
}

/**
 * How many items a collect receipt adds, as the user submitted them. A
 * receipt that collects carries its items in its `collect_assets`
 * operations, and the server names their assets only once its turn applies
 * them (it then names every asset of the family); any other receipt names
 * the assets it answers with.
 */
export function receiptItemCount(
  command: Extract<QuestionnaireTurnCommand, { kind: "collect_receipt" }>,
): number {
  const collects = (command.assetOperations ?? []).flatMap((operation) =>
    operation.op === "collect_assets" ? [operation.items.length] : [],
  );
  return collects.length > 0
    ? collects.reduce((sum, count) => sum + count, 0)
    : command.assetKeys.length;
}

/**
 * The builder's own words for a command a turn carried (plan S4.2). The
 * server's message content is plain English for the models; the chat shows
 * this instead, in the UI language.
 */
export function turnCommandCopy(
  command: QuestionnaireTurnCommand,
  lng?: string,
): string {
  switch (command.kind) {
    case "collect_receipt": {
      // Empty: an attach card the user continued without a file (F19).
      const count = receiptItemCount(command);
      return count === 0
        ? i18n.t("questionnaire.noneAdded", { ns: "app", lng })
        : i18n.t("questionnaire.assetsAdded", { ns: "app", lng, count });
    }
    case "translate":
      return i18n.t("questionnaire.command.translate", {
        ns: "app",
        lng,
        language: languageName(command.language, lng),
      });
    case "use_new_assets":
      return i18n.t("questionnaire.command.useNewAssets", { ns: "app", lng });
    case "set_objective": {
      // The server records what the edit changed on the turn's message; the
      // command a turn is sent with, and older messages, carry only the
      // objective the user set.
      const change = command.change;
      const lines = change ? objectiveChangeLines(change, lng) : [];
      if (lines.length > 0) return lines.join("\n");
      // A recorded change with no words added or removed keeps every
      // objective's words: what the edit can have changed is their order
      // (F26 #10). The bubble shows that order rather than the decision as if
      // it were new. The record says nothing about order (Nest's literal
      // comparison: order is not a change), so the line states the order
      // without claiming it moved.
      const learn = command.studyGoals.learn ?? [];
      if (change && learn.length > 1) {
        return [
          i18n.t("questionnaire.command.objectiveOrder", { ns: "app", lng }),
          ...learn.map((text) => `- ${text}`),
        ].join("\n");
      }
      return i18n.t("questionnaire.command.setObjective", {
        ns: "app",
        lng,
        decision: command.studyGoals.decision,
      });
    }
    case "resume":
      // The Retry of a turn that stopped after its commit: the user asked
      // for the rest again, in the words the failure line offered.
      return i18n.t("questionnaire.turn.retry", { ns: "app", lng });
  }
}
